import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useDebounce } from "@/hooks/useDebounce";
import { routes } from "@/app/navigation/routes";
import { propertySupportService } from "@/features/properties/model/propertyServices";
import type { PropertyRealStateDetailInfo } from "@/features/properties/model/propertiesTypes";
import styles from "./AddressSearch.module.css";

export interface AddressSuggestion {
  id: string;
  address: string;
  city?: string;
  state?: string;
  zipCode?: string;
  apn?: string;
  propertyDetails?: PropertyRealStateDetailInfo;
}

export interface AddressSearchProps {
  placeholder?: string;
  buttonText?: string;
  onAddressSelect?: (address: AddressSuggestion) => void;
  onButtonClick?: (address: string) => void;
  className?: string;
  searchAddresses?: (query: string) => Promise<AddressSuggestion[]>;
}

/**
 * AddressSearch Component
 * Reusable address search component with autocomplete functionality
 * Triggers search after 3+ characters are entered
 */
export const AddressSearch = ({
  placeholder = "Enter address",
  buttonText = "Get Estimate Savings",
  onAddressSelect,
  onButtonClick,
  className = "",
  searchAddresses,
}: AddressSearchProps) => {
  const [inputValue, setInputValue] = useState("");
  const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isFetchingDetails, setIsFetchingDetails] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [selectedAddress, setSelectedAddress] = useState<AddressSuggestion | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);
  const suppressNextFetchRef = useRef(false);
  const navigate = useNavigate();

  const debouncedQuery = useDebounce(inputValue, 300);

  // Check if query has at least 3 characters (including digits, letters, etc.)
  const isValidQuery = debouncedQuery.trim().length >= 3;

  // Default search function (mock - can be replaced with real API)
  const defaultSearchAddresses = useCallback(
    async (query: string): Promise<AddressSuggestion[]> => {
      // Mock delay to simulate API call
      await new Promise((resolve) => setTimeout(resolve, 300));

      // Mock suggestions based on query
      const mockSuggestions: AddressSuggestion[] = [
        {
          id: "1",
          address: `${query} 1/2 Chestnut St`,
          city: "Kearny",
          state: "NJ",
          zipCode: "07032",
        },
        {
          id: "2",
          address: `${query} 1/2 Chestnut St`,
          city: "Kearny",
          state: "NJ",
          zipCode: "07033",
        },
        {
          id: "3",
          address: `${query} 1/2 Chestnut St`,
          city: "Kearny",
          state: "NJ",
          zipCode: "07034",
        },
        {
          id: "4",
          address: `${query} 1/2 Chestnut St`,
          city: "Kearny",
          state: "NJ",
          zipCode: "07035",
        },
      ];

      return mockSuggestions;
    },
    []
  );

  const searchFunction = searchAddresses || defaultSearchAddresses;

  // Fetch suggestions when debounced query changes
  useEffect(() => {
    const fetchSuggestions = async () => {
      // When a user selects a suggestion, we update the input value which would normally
      // trigger another autocomplete request. Suppress that one follow-up fetch.
      if (suppressNextFetchRef.current) {
        suppressNextFetchRef.current = false;
        return;
      }
      if (!isValidQuery) {
        setSuggestions([]);
        setShowSuggestions(false);
        return;
      }

      setIsLoading(true);
      try {
        const results = await searchFunction(debouncedQuery.trim());
        console.log("AddressSearch: Received results:", results);
        console.log("AddressSearch: Results length:", results?.length);
        setSuggestions(results || []);
        // Keep dropdown visible for valid queries even when empty, so we can show "No address found".
        setShowSuggestions(true);
        setSelectedIndex(-1);
      } catch (error) {
        console.error("Error fetching address suggestions:", error);
        setSuggestions([]);
        setShowSuggestions(true);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSuggestions();
  }, [debouncedQuery, isValidQuery, searchFunction]);

  // Handle input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInputValue(value);
    // Only hide suggestions if input becomes too short, otherwise let useEffect handle showing them
    if (value.trim().length < 3) {
      setShowSuggestions(false);
    }
    // Clear selected address if user types something new
    if (selectedAddress) {
      setSelectedAddress(null);
    }
  };

  // Handle suggestion selection
  const handleSelectSuggestion = (suggestion: AddressSuggestion) => {
    const fullAddress = formatAddress(suggestion);
    suppressNextFetchRef.current = true;
    setInputValue(fullAddress);
    setShowSuggestions(false);
    setSelectedIndex(-1);
    setSelectedAddress(suggestion);
    onAddressSelect?.(suggestion);
  };

  // Format address for display
  const formatAddress = (suggestion: AddressSuggestion): string => {
    const parts = [suggestion.address];
    if (suggestion.city) parts.push(suggestion.city);
    if (suggestion.state) parts.push(suggestion.state);
    if (suggestion.zipCode) parts.push(suggestion.zipCode);
    return parts.join(", ");
  };

  // Handle button click
  const handleButtonClick = async () => {
    // Prevent multiple simultaneous calls
    if (isFetchingDetails) {
      return;
    }

    if (inputValue.trim()) {
      onButtonClick?.(inputValue);

      // Navigate to properties route if address is selected
      if (selectedAddress) {
        // Canonical /properties state: { addressData: { address, addressString }, propertyDetails }
        setIsFetchingDetails(true);
        try {
          const fullAddress = formatAddress(selectedAddress);
          let propertyDetails: PropertyRealStateDetailInfo | null = null;

          const navigateAndExit = (details: PropertyRealStateDetailInfo | null) => {
            navigate(routes.properties, {
              state: {
                addressData: {
                  address: selectedAddress,
                  addressString: inputValue,
                },
                propertyDetails: details,
              },
            });
            setIsFetchingDetails(false);
          };

          // Try to fetch property details using APN and ZIP if available
          if (selectedAddress.apn && selectedAddress.zipCode) {
            // First attempt: APN + ZIP + address
            try {
              propertyDetails = await propertySupportService.getRealStatePropertyDetails({
                apn: selectedAddress.apn,
                zip: selectedAddress.zipCode,
                address: fullAddress,
                comps: true,
              });

              console.log("propertyDetails", propertyDetails);
              // Success - navigate and exit immediately, don't call any more APIs
              navigateAndExit(propertyDetails);
              return;
            } catch (apnError) {
              console.warn("Failed to fetch with APN/ZIP, trying ZIP and address:", apnError);

              // Second attempt: ZIP + address (only if first failed)
              try {
                propertyDetails = await propertySupportService.getRealStatePropertyDetails({
                  address: fullAddress,
                  zip: selectedAddress.zipCode,
                  comps: true,
                });
                // Success - navigate and exit immediately, don't call any more APIs
                navigateAndExit(propertyDetails);
                return;
              } catch (zipError) {
                console.warn("Failed to fetch with ZIP, trying address only:", zipError);

                // Third attempt: address only (only if previous attempts failed)
                try {
                  propertyDetails = await propertySupportService.getRealStatePropertyDetails({
                    address: fullAddress,
                    comps: true,
                  });
                  // Success - navigate and exit immediately
                  navigateAndExit(propertyDetails);
                  return;
                } catch (addressError) {
                  console.error("Failed to fetch property details:", addressError);
                  // Show toast only for the last fallback failure
                  // toastService.show("Failed to fetch property details. Please try again.", "error");
                  // Navigate with null since all attempts failed
                  navigateAndExit(null);
                  return;
                }
              }
            }
          } else if (selectedAddress.zipCode) {
            // First attempt: ZIP + address
            try {
              propertyDetails = await propertySupportService.getRealStatePropertyDetails({
                address: fullAddress,
                zip: selectedAddress.zipCode,
                comps: true,
              });
              // Success - navigate and exit immediately, don't call any more APIs
              navigateAndExit(propertyDetails);
              return;
            } catch (zipError) {
              console.warn("Failed to fetch with ZIP, trying address only:", zipError);

              // Second attempt: address only (only if first failed)
              try {
                propertyDetails = await propertySupportService.getRealStatePropertyDetails({
                  address: fullAddress,
                  comps: true,
                });
                // Success - navigate and exit immediately
                navigateAndExit(propertyDetails);
                return;
              } catch (addressError) {
                console.error("Failed to fetch property details:", addressError);
                // Show toast only for the last fallback failure
                // toastService.show("Failed to fetch property details. Please try again.", "error");
                // Navigate with null since all attempts failed
                navigateAndExit(null);
                return;
              }
            }
          } else {
            // Only attempt: address only
            try {
              propertyDetails = await propertySupportService.getRealStatePropertyDetails({
                address: fullAddress,
                comps: true,
              });
              // Success - navigate and exit immediately
              navigateAndExit(propertyDetails);
              return;
            } catch (addressError) {
              console.error("Failed to fetch property details:", addressError);
              // Show toast only for the last fallback failure
              // toastService.show("Failed to fetch property details. Please try again.", "error");
              // Navigate with null since attempt failed
              navigateAndExit(null);
              return;
            }
          }
        } catch (error) {
          console.error("Error fetching property details:", error);
          // Show toast for unexpected errors
          // toastService.show("Failed to fetch property details. Please try again.", "error");
          // Navigate anyway with basic address info if API fails
          setIsFetchingDetails(false);
        }
      }
    }
  };

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showSuggestions || suggestions.length === 0) {
      if (e.key === "Enter") {
        handleButtonClick();
      }
      return;
    }

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setSelectedIndex((prev) => (prev < suggestions.length - 1 ? prev + 1 : prev));
        break;
      case "ArrowUp":
        e.preventDefault();
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
        break;
      case "Enter":
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
          const selectedSuggestion = suggestions[selectedIndex];
          if (selectedSuggestion) {
            handleSelectSuggestion(selectedSuggestion);
          }
        } else {
          handleButtonClick();
        }
        break;
      case "Escape":
        setShowSuggestions(false);
        setSelectedIndex(-1);
        inputRef.current?.blur();
        break;
    }
  };

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setShowSuggestions(false);
        setSelectedIndex(-1);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div className={`${styles.searchContainer} ${className}`}>
      <div className={styles.inputWrapper}>
        <svg
          className={styles.locationIcon}
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"
            fill="currentColor"
          />
        </svg>
        <input
          ref={inputRef}
          type="text"
          className={styles.addressInput}
          placeholder={placeholder}
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            // Show suggestions if we have valid query and suggestions exist
            if (isValidQuery && suggestions.length > 0) {
              setShowSuggestions(true);
            }
          }}
        />
        <button
          className={styles.estimateButton}
          onClick={handleButtonClick}
          disabled={!selectedAddress || isFetchingDetails}
        >
          {isFetchingDetails ? "Loading..." : buttonText}
        </button>
        {showSuggestions && (
          <div ref={suggestionsRef} className={styles.suggestionsContainer}>
            {isLoading && <div className={styles.loadingMessage}>Loading suggestions...</div>}
            {!isLoading && suggestions.length === 0 && isValidQuery && (
              <div className={styles.loadingMessage}>No address found</div>
            )}
            {!isLoading &&
              suggestions.length > 0 &&
              suggestions.map((suggestion, index) => (
                <div
                  key={suggestion.id}
                  className={`${styles.suggestionItem} ${
                    index === selectedIndex ? styles.suggestionItemActive : ""
                  }`}
                  onClick={() => handleSelectSuggestion(suggestion)}
                  onMouseEnter={() => setSelectedIndex(index)}
                >
                  {formatAddress(suggestion)}
                </div>
              ))}
          </div>
        )}
      </div>
    </div>
  );
};
